// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import {
  activeAttentionSeveritySchema,
  attentionReasonSchema,
} from '@/constants/attention';
import { caseInsensitiveLowerEnum } from '@/constants/case-insensitive-enum';
import { trackingNumberDetailsSchema } from '@/constants/shipping-carrier';
import { currencySchema } from '@/currency/api/types';
import { cycleTimeMetricSchema } from '@/report/api/types';
import {
  sparseFulfillmentSchema,
  sparseItemSchema,
  sparseLocationWithIntegrationSchema,
  sparsePOSchema,
  sparsePersonSchema,
  sparseROSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { decimalDaysSchema } from '@/utilities/decimal-days-schema';
import { z } from 'zod';

/**
 * Enum for shipment status options.
 * Lowercase for URL parameters, uppercase for server API.
 */
export type ShipmentStatus =
  | 'open'
  | 'shipped'
  | 'delivered'
  | 'received'
  | 'cancelled'
  | 'all';

/**
 * Schema for shipment status used in filtering.
 */
export const shipmentStatusSchema = z.enum([
  'open',
  'shipped',
  'delivered',
  'received',
  'cancelled',
  'all',
]);

/**
 * Maps lowercase status values to uppercase for server API.
 */
export const statusToUpperCase: Record<ShipmentStatus, string> = {
  open: 'OPEN',
  shipped: 'SHIPPED',
  delivered: 'DELIVERED',
  received: 'RECEIVED',
  cancelled: 'CANCELLED',
  all: 'ALL',
};

/**
 * Schema for shipment type used in filtering.
 * Backend sends UPPERCASE, but we transform to lowercase for frontend use.
 */
const shipmentTypeSchema = z
  .enum(['INBOUND', 'OUTBOUND'])
  .transform((val) => val.toLowerCase() as 'inbound' | 'outbound');

/**
 * Type for shipment types.
 */
export type ShipmentType = z.infer<typeof shipmentTypeSchema>;

/**
 * Schema for a shipment item.
 * Uses nested item structure where line item identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 * Note: Shipment items don't have separate binding IDs - id/key match the item's id/key.
 */
export const shipmentItemSchema = z.object({
  // Line item identifiers (same as item id/key for shipments)
  id: z.string(),
  key: z.string(),
  // Nested catalog item properties
  item: sparseItemSchema,
  quantity: z.number().nullable(),
  receivedQuantity: z.number().nullable(),
  externalReference: z.string().nullable(),
  value: z.string().nullable(),
  estimatedValue: z.string().nullable(),
  serials: z.array(z.string()),
  pendingSerials: z.array(z.string()),
  serialCount: z.number(),
  pendingSerialCount: z.number(),
  // Units the 3PL reported on this line that are held pending resolution. Held
  // units are excluded from quantity and serials.
  quarantinedQuantity: z.number(),
  // What the held units are estimated to be worth. Held units have no asset, so
  // the line's own value never covers them.
  quarantinedEstimatedValue: z.string().nullable(),
});

export type ShipmentItem = z.infer<typeof shipmentItemSchema>;

export const quarantineResolutionKindSchema = z.enum([
  'KEPT_HARDFIN_ITEM',
  'CORRECTED_SERIAL',
  'CREATED_ASSET',
  'ACCEPTED_SERIAL',
]);

export type QuarantineResolutionKind = z.infer<
  typeof quarantineResolutionKindSchema
>;

// Why a resolution the hold's type allows cannot be run right now.
export const quarantineResolutionBlockReasonSchema = z.enum([
  'SERIAL_ALREADY_TAKEN',
]);

export type QuarantineResolutionBlockReason = z.infer<
  typeof quarantineResolutionBlockReasonSchema
>;

const quarantineResolutionOptionSchema = z.object({
  kind: quarantineResolutionKindSchema,
  isEnabled: z.boolean(),
  disabledReason: quarantineResolutionBlockReasonSchema.nullable(),
});

export type QuarantineResolutionOption = z.infer<
  typeof quarantineResolutionOptionSchema
>;

const quarantineResolutionSchema = z.object({
  kind: quarantineResolutionKindSchema,
  resolvedAt: dateTimeSchema,
  resolvedBy: sparsePersonSchema,
  note: z.string(),
  // The unit the resolution settled on: the corrected asset for a serial
  // correction, the created asset for a creation, null for a keep.
  resolvedAsset: z
    .object({ id: z.string(), key: z.string(), serial: z.string() })
    .nullable(),
});

// Why a serial is held. The audit trail imports this rather than keeping a
// second copy.
export const quarantineTypeSchema = z.enum([
  'SKU_TRANSMUTATION',
  'UNKNOWN_SERIAL',
  'SERIAL_PATTERN_MISMATCH',
  'DUPLICATE_IN_ORDER',
  'DUPLICATE_ALREADY_EXISTS',
  'MISSING_SERIAL',
]);

export const quarantinedSerialSchema = z.object({
  key: z.string(),
  serial: z.string(),
  quarantineType: quarantineTypeSchema,
  // Every resolution this hold's type allows, and whether each can run.
  resolutionOptions: z.array(quarantineResolutionOptionSchema),
  reportedItem: sparseItemSchema,
  // The item Hardfin already has the serial under; null for a hold on a serial
  // Hardfin has no record of.
  existingItem: sparseItemSchema.nullable(),
  // The asset the held serial already exists as, so the panel can link to it.
  existingAsset: z
    .object({ id: z.string(), key: z.string(), serial: z.string() })
    .nullable(),
  reportedLineReference: z.string().nullable(),
  reportedAt: dateTimeSchema,
  resolution: quarantineResolutionSchema.nullable(),
  // The order the held serial's shipment belongs to and which kind of order that
  // is, plus whether that order carries a line for the Hardfin item and what the
  // line still has outstanding.
  orderNumber: z.string().nullable(),
  orderType: z
    .enum(['FULFILLMENT_ORDER', 'PURCHASE_ORDER', 'RETURN_ORDER'])
    .nullable(),
  matchingOpenQuantity: z.number(),
  hasMatchingLine: z.boolean(),
});

export type QuarantinedSerial = z.infer<typeof quarantinedSerialSchema>;

/**
 * Schema for shipment display status (returned by backend).
 * Excludes 'all' which is only used for filtering.
 */
const shipmentDisplayStatusSchema = caseInsensitiveLowerEnum([
  'open',
  'shipped',
  'delivered',
  'received',
  'cancelled',
]);

export type ShipmentDisplayStatus = z.infer<typeof shipmentDisplayStatusSchema>;

export const shipmentCycleTimeSchema = z.object({
  createdToShippedDays: decimalDaysSchema,
  shippedToDeliveredDays: decimalDaysSchema,
});

const shipmentCycleTimeAttentionSchema = z.object({
  severity: activeAttentionSeveritySchema,
  reason: attentionReasonSchema,
});

const shipmentAttentionSchema = z.object({
  cycleTime: shipmentCycleTimeAttentionSchema,
});

export const shipmentSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    status: shipmentDisplayStatusSchema,
    shipmentNumber: z.string(),
    purchaseOrder: sparsePOSchema.nullable(),
    returnOrder: sparseROSchema.nullable(),
    fulfillmentOrder: sparseFulfillmentSchema.nullable(),
    trackingNumberDetails: trackingNumberDetailsSchema.nullable(),
    totalValue: z.string().nullable(),
    estimatedTotalValue: z.string().nullable(),
    fromLocation: sparseLocationWithIntegrationSchema.nullable(),
    toLocation: sparseLocationWithIntegrationSchema.nullable(),
    items: z.array(shipmentItemSchema),
    // Serials the 3PL reported that conflict with the asset record, held for
    // per-serial resolution. Empty when the shipment has no holds.
    quarantinedSerials: z.array(quarantinedSerialSchema),
    // Whether this shipment is holding any quarantined serials (maintained flag).
    hasQuarantinedItems: z.boolean(),
    // Whether a provider holds this shipment, read from the 3PL binding
    thirdPartyBound: z.boolean(),
    // When the provider stopped reporting this shipment; null while it reports it
    missingSince: dateTimeSchema.nullable(),
    shipmentType: shipmentTypeSchema.optional(),
    shippedAt: dateTimeSchema.optional().nullable(),
    shippedAtTimezone: z.string().optional().nullable(),
    expectedShippedOn: dateSchema.optional().nullable(),
    expectedDeliveredOn: dateSchema.optional().nullable(),
    deliveredAt: dateTimeSchema.optional().nullable(),
    deliveredAtTimezone: z.string().optional().nullable(),
    receivedAt: dateTimeSchema.nullable(),
    receivedAtTimezone: z.string().optional().nullable(),
    deliveryNote: z.string().nullable(),
    receiptNote: z.string().nullable(),
    shipmentNote: z.string().nullable(),
    shippingCost: currencySchema.nullable(),
    createdBy: personSchema,
    createdAt: dateTimeSchema,
    cycleTime: shipmentCycleTimeSchema,
    cancelledAt: dateTimeSchema.nullable().optional(),
    cancelledBy: z.string().uuid().nullable().optional(),
    cancellationReason: z.string().nullable().optional(),
    // Tracking-level metadata sourced from the latest matching Shippo
    // webhook payload, joined into the listing query on the backend. Both
    // are optional + nullable: a shipment may not have a carrier yet, may
    // have a carrier but no Shippo data, or Shippo may not report these.
    serviceLevelName: z.string().optional().nullable(),
    eta: dateTimeSchema.optional().nullable(),
    // Current status age in elapsed decimal days. Null means the shipment is in
    // a terminal state with no running clock. The API sends decimal days as a
    // string and the schema parses it at the boundary.
    timeInStatusDays: decimalDaysSchema,
    // Needs-attention (cycle-time stall) signal computed live on the backend.
    // Null means the shipment does not need attention.
    attention: shipmentAttentionSchema.nullable(),
  })
  .transform((data) => {
    // Handle shippedAt with proper timezone localization
    let shippedAt = null;
    if (data.shippedAt) {
      if (data.shippedAtTimezone) {
        // If timezone is specified, parse the UTC timestamp and convert to the specified timezone
        // This ensures the timestamp is displayed in the correct local time
        shippedAt = data.shippedAt.setZone(data.shippedAtTimezone);
      } else {
        // If no timezone, parse as local time (user's browser timezone)
        // This ensures the timestamp defaults to the user's local time
        shippedAt = data.shippedAt;
      }
    }

    // Handle deliveredAt with proper timezone localization
    let deliveredAt = null;
    if (data.deliveredAt) {
      if (data.deliveredAtTimezone) {
        // If timezone is specified, parse the UTC timestamp and convert to the specified timezone
        // This ensures the timestamp is displayed in the correct local time
        deliveredAt = data.deliveredAt.setZone(data.deliveredAtTimezone);
      } else {
        // If no timezone, parse as UTC and keep the date component as-is
        // This preserves the date value (e.g., "2025-11-03T00:00:00Z" stays as Nov 3)
        deliveredAt = data.deliveredAt;
      }
    }

    // Handle receivedAt with proper timezone localization
    let receivedAt = null;
    if (data.receivedAt) {
      if (data.receivedAtTimezone) {
        // If timezone is specified, parse the UTC timestamp and convert to the specified timezone
        // This ensures the timestamp is displayed in the correct local time
        receivedAt = data.receivedAt.setZone(data.receivedAtTimezone);
      } else {
        // If no timezone, parse as-is
        receivedAt = data.receivedAt;
      }
    }

    return {
      ...data,
      shippedAt,
      deliveredAt,
      receivedAt,
    };
  });

export type Shipment = z.infer<typeof shipmentSchema>;

/**
 * Determines the shipment type from shipment data.
 * If shipmentType is set, returns it directly.
 * Otherwise infers from the related order:
 * - Return orders and purchase orders create inbound shipments
 * - Fulfillment orders create outbound shipments
 */
export function getShipmentType(
  shipment: Pick<
    Shipment,
    'shipmentType' | 'returnOrder' | 'purchaseOrder' | 'fulfillmentOrder'
  >
): ShipmentType {
  if (shipment.shipmentType) {
    return shipment.shipmentType;
  }
  // Infer from related order
  if (shipment.returnOrder || shipment.purchaseOrder) {
    return 'inbound';
  }
  return 'outbound';
}

export const pagedShipmentSchema = z.object({
  statusCounts: z.record(z.number()),
  results: z.array(shipmentSchema),
  totalCount: z.number(),
  needsAttentionCount: z.number(),
  quarantinedCount: z.number(),
});

export const outboundShipmentCycleTimeReportSchema = z.object({
  createdToShipped: cycleTimeMetricSchema,
  shippedToDelivered: cycleTimeMetricSchema,
  totalShipments: z.number(),
});

export type OutboundShipmentCycleTimeReport = z.infer<
  typeof outboundShipmentCycleTimeReportSchema
>;

export const inboundShipmentCycleTimeReportSchema = z.object({
  createdToShipped: cycleTimeMetricSchema,
  shippedToDelivered: cycleTimeMetricSchema,
  deliveredToReceived: cycleTimeMetricSchema,
  totalShipments: z.number(),
});

export type InboundShipmentCycleTimeReport = z.infer<
  typeof inboundShipmentCycleTimeReportSchema
>;
